import { useChargebee } from '@/plugins/useChargebee'
import { useSolidgate } from '@/plugins/useSolidgate'
import type { PaymentProvider, PaymentProviderAPI, ProviderType } from '@/types'
import { PaymentProviders, PaymentType } from '@/utils/helper-objects'
import { computed, ref } from 'vue'

/**
 * Handles payment provider integration with multiple PSPs.
 * Supports Chargebee and Solidgate providers.
 *
 * @param {ProviderType} providerType - The initial payment provider to use
 * @returns {PaymentProviderAPI} Payment provider API methods and properties
 */
export function usePaymentProvider(
  providerType: ProviderType = PaymentProviders.Chargebee
): PaymentProviderAPI {
  // Store current provider
  const provider = ref<ProviderType>(providerType)

  // Create provider instances
  const chargebeeProvider = useChargebee()
  const solidgateProvider = useSolidgate()

  // Get the active provider based on selection
  const activeProvider = computed<PaymentProvider>(() => {
    return provider.value === PaymentProviders.Chargebee
      ? chargebeeProvider
      : solidgateProvider
  })

  /**
   * Mounts the payment form for the active provider
   *
   * @returns {Promise<any>}
   */
  const mount = async (): Promise<any> => {
    return activeProvider.value.mount()
  }

  /**
   * Handles successful payment authorization
   *
   * @param {any} payment - Payment data
   * @param {any} data - Additional data
   * @returns {Promise<any>}
   */
  const handlePaymentAuthorized = async (
    payment: any,
    data: any
  ): Promise<any> => {
    return activeProvider.value
      .handlePaymentAuthorized(payment, data)
      .then((response: any) => {
        if (
          data.paymentType === PaymentType.Subscription &&
          !localStorage.getItem('show-subscription-onboarding')
        ) {
          localStorage.setItem('show-subscription-onboarding', 'true')
        } else if (
          data.paymentType === PaymentType.InstantWin &&
          !localStorage.getItem('show-instant-wins-onboarding')
        ) {
          localStorage.setItem('show-instant-wins-onboarding', 'true')
        }

        return response
      })
  }

  /**
   * Handles payment errors
   *
   * @param {any} error - Error details
   * @returns {any}
   */
  const handlePaymentError = (error: any): any => {
    return activeProvider.value.handlePaymentError(error)
  }

  // Expose provider errors
  const errors = computed(() => activeProvider.value.errors.value)

  const clearErrors = (): void => {
    if (activeProvider.value.errors.value) {
      activeProvider.value.errors.value = null
    }
  }

  /**
   * Changes the active payment provider
   *
   * @param {ProviderName} newProvider - Provider to switch to
   */
  const setProvider = (newProvider: ProviderType): void => {
    provider.value = newProvider
  }

  return {
    clearErrors,
    provider,
    setProvider,
    activeProvider,
    mount,
    handlePaymentAuthorized,
    handlePaymentError,
    errors,
  }
}
